import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeLink } from '@/components/ui/tee-link';
import type { HowTo, PostTemplate } from '../currentWeek.social';

// A single guide/template slide-over used by both the Tasks list (task guides)
// and the Social tab (post templates + platform how-tos). Mirrors the
// prototype's right drawer: gold eyebrow, title, intro, gold-dot tip list,
// optional template fields + how-to steps, and a forest "Playbook" link.
export interface GuideContent {
  eyebrow?: string; // e.g. 'Guide' | 'Template'
  title: string;
  intro?: string;
  bulletsTitle?: string;
  bullets?: string[];
  template?: PostTemplate;
  howTos?: HowTo[];
  playbookAnchor?: string;
}

const microLabel =
  'text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]';

function TemplateField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className={microLabel}>{label}</span>
      <p className="text-body-s text-[var(--text-content-title)]">{value}</p>
    </div>
  );
}

export function GuideDrawer({
  content,
  onClose,
}: {
  content: GuideContent | null;
  onClose: () => void;
}) {
  if (!content) return null;
  const { template } = content;
  return (
    <TeeFormModal
      isOpen={!!content}
      title={content.title}
      onClose={onClose}
      onPrimaryAction={onClose}
      primaryActionLabel="Done"
    >
      <div className="flex flex-col gap-5">
        {content.eyebrow && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-gold)]">
            {content.eyebrow}
          </span>
        )}

        {content.intro && (
          <p className="text-body-m text-[var(--text-content-title)]">
            {content.intro}
          </p>
        )}

        {template && (
          <div className="flex flex-col gap-4">
            <div className="rounded-[8px] bg-[var(--background-page-header)] p-3 flex flex-col gap-1">
              <span className={microLabel}>Script</span>
              <p className="text-body-s italic text-[var(--text-content-title)]">
                {template.script}
              </p>
            </div>
            <TemplateField label="How to shoot it" value={template.howTo} />
            <TemplateField label="Who's in it" value={template.who} />
            <TemplateField label="Style" value={template.style} />
            <TemplateField label="Call to action" value={template.cta} />
          </div>
        )}

        {content.bullets && content.bullets.length > 0 && (
          <div className="flex flex-col gap-2">
            {content.bulletsTitle && (
              <span className={microLabel}>{content.bulletsTitle}</span>
            )}
            <ul className="flex flex-col gap-2">
              {content.bullets.map((b, i) => (
                <li
                  key={i}
                  className="text-body-s text-[var(--text-content-title)] flex gap-2.5"
                >
                  <span className="text-[var(--color-text-gold)] leading-5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {content.howTos && content.howTos.length > 0 && (
          <div className="flex flex-col gap-4">
            <span className={microLabel}>How to post it</span>
            {content.howTos.map((h, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <p className="text-body-s font-medium text-[var(--text-content-title)]">
                  {h.name}
                </p>
                <ol className="flex flex-col gap-1.5">
                  {h.steps.map((s, j) => (
                    <li
                      key={j}
                      className="text-body-s text-[var(--text-content-sub-title)] flex gap-2.5"
                    >
                      <span className="text-[var(--color-text-gold)]">{j + 1}.</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}

        {content.playbookAnchor && (
          <div className="pt-1">
            <TeeLink
              variant="primary"
              size="s"
              href={`/game-plan/playbook#${content.playbookAnchor}`}
            >
              Read more in the Playbook →
            </TeeLink>
          </div>
        )}
      </div>
    </TeeFormModal>
  );
}
